<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import FormChartVisualization from '@/components/forms/charts/FormChartVisualization.vue';
import FormChartDataTable from '@/components/forms/charts/FormChartDataTable.vue';

const props = defineProps({
    initialFormId: {
        type: Number,
        required: true,
    },
    initialChartId: {
        type: Number,
        required: true,
    },
    formTitle: {
        type: String,
        required: true,
    },
    chartName: {
        type: String,
        default: '',
    },
    initialUsers: {
        type: Array,
        default: () => [],
    },
});

const loading = ref(false);
const chartData = ref(null);
const loadError = ref(null);
const selectedUserId = ref('all');

const userOptions = computed(() => {
    const allOption = [{ label: _mft('form:chart.detail.allUsers'), value: 'all' }];
    const userOpts = props.initialUsers.map((user) => ({
        label: user.name ? `${user.name} (${user.email})` : user.email,
        value: String(user.id),
    }));
    return [...allOption, ...userOpts];
});

const chartTitle = computed(() => props.chartName || props.formTitle);

const loadChartData = async () => {
    loading.value = true;
    loadError.value = null;

    try {
        const params = {};
        if (selectedUserId.value !== 'all') {
            params.user_id = selectedUserId.value;
        }

        const response = await axios.get(
            route('api.form.chart.data', {
                id: props.initialFormId,
                chartId: props.initialChartId,
            }),
            { params }
        );

        chartData.value = response.data;
    } catch (error) {
        loadError.value = _mft('form:chart.detail.loadError');
    } finally {
        loading.value = false;
    }
};

watch(selectedUserId, () => {
    loadChartData();
});

onMounted(async () => {
    await loadChartData();
});
</script>

<template>
    <div>
        <div class="md:sticky top-2 z-topbar">
            <frosted-bar>
                <div class="flex items-center justify-between space-x-4">
                    <h1>{{ chartTitle }}</h1>
                </div>

                <template #navigation>
                    <a
                        :href="route('forms.edit', { id: initialFormId })+'#charts'"
                        class="mf-link text-xs"
                    >{{ formTitle }}</a>
                </template>
            </frosted-bar>
        </div>

        <div class="mt-12">
            <div class="mb-6 max-w-sm">
                <input-select
                    v-model:content="selectedUserId"
                    :label="_mft('form:chart.detail.filterByUser')"
                    :options="userOptions"
                />
            </div>

            <div v-if="loading" class="flex justify-center">
                <mf-spinner-medium class="w-24 h-12" />
            </div>

            <p v-else-if="loadError" class="text-sm text-danger">{{ loadError }}</p>

            <div v-else-if="chartData" class="space-y-4">
                <div class="text-sm text-gray-600">
                    {{ _mft('form:chart.submissions') }} {{ chartData.summary?.submission_count ?? 0 }} ·
                    {{ _mft('form:chart.valuesCounted') }} {{ chartData.summary?.value_count ?? 0 }}
                </div>

                <form-chart-visualization
                    v-if="chartData.chart?.chart_type !== 'table'"
                    :class="chartData.chart?.chart_type === 'donut' ? 'max-w-md' : 'max-w-4xl'"
                    :chart-type="chartData.chart?.chart_type"
                    :payload="chartData"
                />

                <div v-if="chartData.chart?.chart_type === 'table'">
                    <h5 class="mb-2 text-sm font-medium">{{ _mft('form:chart.table') }}</h5>
                    <form-chart-data-table
                        :chart-type="chartData.chart?.chart_type"
                        :payload="chartData"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
